import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { CFASTButton } from '../modules/cbtCommon';

// Every account must accept the Terms of Use and Privacy Policy. New
// accounts do it with the signup checkbox; accounts made before that (and
// sign-ups from older app versions) get this window over every page of the
// site while signed in (App.js renders it), until they accept. It can't be
// closed any other way, and once accepted it never shows again.
const TermsAcceptanceGate = () => {
  const { profile, acceptTermsPrivacy, signOut } = useAuth();
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (profile?.accepted_terms_privacy !== false) return null;

  const accept = async () => {
    setBusy(true);
    setError(null);
    const { error: acceptError } = await acceptTermsPrivacy();
    setBusy(false);
    if (acceptError) setError(acceptError.message || 'That didn’t save. Please try again.');
  };

  const keepOpen = (event) => event.preventDefault();

  return (
    <Dialog.Root open>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-[2px]" />
        <Dialog.Content
          onEscapeKeyDown={keepOpen}
          onPointerDownOutside={keepOpen}
          onInteractOutside={keepOpen}
          className="fixed left-1/2 top-1/2 z-[201] w-[calc(100%_-_2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border border-[#334c65] bg-[#0a1524] p-5 text-white shadow-[0_24px_80px_rgba(0,0,0,0.72)] outline-none sm:p-6"
          data-testid="terms-acceptance-dialog"
        >
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#36536b] bg-[#0e2035] text-[#68d9ff]">
              <ShieldCheck size={16} aria-hidden="true" />
            </span>
            <div>
              <Dialog.Title className="text-base font-semibold text-white">Terms of Use and Privacy Policy</Dialog.Title>
              <Dialog.Description className="mt-1 text-sm leading-relaxed text-[#9db1c5]">
                To keep using CBAT Academy, you need to read and accept our Terms of Use and Privacy Policy.
              </Dialog.Description>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold">
            <a className="text-[#68d9ff] underline" href="/terms" target="_blank" rel="noopener noreferrer">Read the Terms of Use</a>
            <a className="text-[#68d9ff] underline" href="/privacy" target="_blank" rel="noopener noreferrer">Read the Privacy Policy</a>
          </div>

          <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-[#dbe7f2]">
            <input
              type="checkbox"
              checked={checked}
              onChange={(event) => setChecked(event.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-[#68d9ff]"
              data-testid="terms-acceptance-checkbox"
            />
            <span>I confirm I have read and accept the Terms of Use and Privacy Policy, and I am 16 or older.</span>
          </label>

          {error && (
            <p className="mt-3 text-xs text-[#ffb3bb]" role="alert">{error}</p>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              className="text-xs font-semibold text-[#8fa5bd] underline hover:text-white"
              onClick={() => void signOut()}
              disabled={busy}
            >
              Sign out
            </button>
            <CFASTButton
              className="site-button--primary"
              onClick={() => void accept()}
              disabled={!checked || busy}
              testid="terms-acceptance-accept"
            >
              {busy ? 'Saving…' : 'Accept and continue'}
            </CFASTButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default TermsAcceptanceGate;
